import React, { useState, useEffect } from 'react';
import { HiSearch, HiTrash } from 'react-icons/hi';
import Layout from '../components/Layout';
import Loader from '../components/Loader';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { useNotification } from '../context/NotificationContext';
import { getErrorMessage } from '../utils/errorHandler';
import { formatDate } from '../utils/dateFormatter';
import { formatPKR } from '../utils/currencyFormatter';
import { API } from '../config';
import axios from 'axios';
import './DeletedData.css';

const DeletedParties = () => {
  const { user } = useAuth();
  const { effectiveBranchId } = useBranch();
  const { error } = useNotification();
  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusinessId, setSelectedBusinessId] = useState(user?.business_id || '');

  useEffect(() => {
    if (user?.role === 'super_admin') {
      axios.get(`${API}/businesses/all`)
        .then(res => {
          const list = res.data.businesses || [];
          setBusinesses(list);
          if (!selectedBusinessId && list.length > 0) setSelectedBusinessId(list[0].id);
        })
        .catch(() => {});
    }
  }, [user?.role]);

  useEffect(() => {
    const businessId = user?.role === 'super_admin' ? selectedBusinessId : user?.business_id;
    if (!businessId) {
      setLoading(false);
      setParties([]);
      return;
    }
    setLoading(true);
    const params = effectiveBranchId ? `?branchId=${effectiveBranchId}` : '';
    axios.get(`${API}/parties/business/${businessId}/deleted${params}`)
      .then(res => setParties(res.data.parties || []))
      .catch(err => error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [selectedBusinessId, user?.role, user?.business_id, effectiveBranchId]);

  const filtered = parties.filter(p =>
    !searchTerm ||
    (p.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.phone || '').includes(searchTerm) ||
    (p.city || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRoleBadges = (party) => {
    const roles = [];
    if (party.is_vendor) roles.push('Vendor');
    if (party.is_customer) roles.push('Customer');
    if (party.is_tailor) roles.push('Tailor');
    if (party.is_additional) roles.push('Additional');
    return roles.join(', ') || '—';
  };

  return (
    <Layout>
      <div className="deleted-data-page">
        <div className="page-header">
          <div>
            <h1><HiTrash className="page-icon" /> Deleted Parties</h1>
            <p className="page-subtitle">View soft-deleted parties</p>
          </div>
        </div>
        <div className="filters-container">
          {user?.role === 'super_admin' && (
            <div className="filter-dropdown">
              <select value={selectedBusinessId} onChange={(e) => setSelectedBusinessId(e.target.value)}>
                <option value="">Select Business</option>
                {businesses.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
          )}
          <div className="search-bar">
            <HiSearch className="search-icon" />
            <input
              placeholder="Search by name, phone, or city..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        {loading ? (
          <Loader message="Loading deleted parties..." variant="block" />
        ) : (
          <div className="table-container">
            {filtered.length === 0 ? (
              <div className="empty-state">
                <p>No deleted parties found.</p>
              </div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>City</th>
                    <th>Roles</th>
                    <th>Balance</th>
                    <th>Deleted At</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(p => (
                    <tr key={p.id}>
                      <td className="font-semibold">{p.name}</td>
                      <td>{p.phone || '—'}</td>
                      <td>{p.city || '—'}</td>
                      <td>{getRoleBadges(p)}</td>
                      <td>{formatPKR(p.balance || 0)}</td>
                      <td>{formatDate(p.deleted_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default DeletedParties;
